import { useQueryClient } from "@tanstack/react-query";
import ProcessSelfBilledDraftInvoiceForm
	from "modules/billing/components/PendingInvoicesIndexView/components/PendingInvoiceDetailsSidebar/components/ProcessSelfBilledDraftInvoiceForm";
import StartRegularInvoiceBillingProcessForm
	from "modules/billing/components/PendingInvoicesIndexView/components/PendingInvoiceDetailsSidebar/components/StartRegularInvoiceBillingProcessForm";
import { useCallback, useMemo, useState } from "react";

import Sidebar from "~/components/Sidebar";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import { useAggregatedDataV2 } from "~/hooks/useAggregatedDataV2.ts";
import { ALL_DRAFT_INVOICES_QUERY_KEY } from "~/modules/billing/api/draftInvoice/draftInvoiceQueries.ts";
import type { InvoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import { invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import {
	All_PENDING_INVOICES_QUERY_KEY,
	useAllPendingInvoices,
} from "~/modules/billing/api/pendingInvoice/pendingInvoiceQueries.ts";
import { ALL_PROCESSED_INVOICES_QUERY_KEY } from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import PendingInvoiceDetails
	from "~/modules/billing/components/PendingInvoicesIndexView/components/PendingInvoiceDetailsSidebar/components/PendingInvoiceDetails";
import { createUnprocessedInvoiceTableDataFromProjectInvoice } from "~/modules/billing/utils/billingUtils.ts";
import { useAllUsers } from "~/modules/user/api/user/userQueries.ts";

type Props = {
	isOpen: boolean;
	onClose: () => void;
	invoiceId?: string;
	userCanManageInvoices: boolean;
};

const defaultInvoiceTypeId: InvoiceTypeId = invoiceTypeId.RegularInvoice;

const PendingInvoiceDetailsSidebar = ({
	isOpen,
	onClose,
	invoiceId,
	userCanManageInvoices,
}: Props) => {
	const [showStartBillingProcessForm, setShowStartBillingProcessForm] = useState<boolean>(false);
	const [selectedInvoiceTypeId, setSelectedInvoiceTypeId] = useState<InvoiceTypeId>(defaultInvoiceTypeId);
	const queryClient = useQueryClient();

	const handleClose = useCallback(() => {
		setShowStartBillingProcessForm(false);
		setSelectedInvoiceTypeId(defaultInvoiceTypeId);
		onClose();
	}, [onClose]);

	const handleSuccess = useCallback(async () => {

		queryClient.invalidateQueries({ queryKey: ALL_PROCESSED_INVOICES_QUERY_KEY });

		queryClient.invalidateQueries({ queryKey: ALL_DRAFT_INVOICES_QUERY_KEY });

		await queryClient.refetchQueries({ queryKey: All_PENDING_INVOICES_QUERY_KEY });

		handleClose();
	}, [handleClose, queryClient]);

	const { data, options } = useAggregatedDataV2({
		allUsers: useAllUsers(),
		allPendingInvoices: useAllPendingInvoices(),
	});

	const {
		allPendingInvoices, allUsers,
	} = data;

	const enrichedInvoiceData = useMemo(() => {
		if (allPendingInvoices) {
			const invoice = allPendingInvoices.projectInvoices.find((inv) => inv.id === invoiceId);
			if (invoice) {
				return createUnprocessedInvoiceTableDataFromProjectInvoice(invoice, allPendingInvoices.projectPhases, allPendingInvoices.projects);
			}
		}
		return null;
	}, [allPendingInvoices, invoiceId]);

	const showDetails = isOpen && !showStartBillingProcessForm && enrichedInvoiceData && allUsers && !options.isLoading;
	const readyToProceed = isOpen && showStartBillingProcessForm && enrichedInvoiceData && allUsers && !options.isLoading;
	const showStartRegularInvoiceBillingProcessForm = readyToProceed && selectedInvoiceTypeId === invoiceTypeId.RegularInvoice;
	const showUpdateSelfBilledInvoiceStatusForm = readyToProceed && selectedInvoiceTypeId === invoiceTypeId.SelfBilledInvoice;
	return (
		<Sidebar closeOnOutsideClick={!showStartBillingProcessForm}
				 open={isOpen}
				 setOpen={handleClose}>
			{options.isLoading && <SidebarBusyOverlay />}
			{showDetails &&
				<PendingInvoiceDetails allUsersData={allUsers}
									   onClose={handleClose}
									   onStartBillingProcessClick={() => setShowStartBillingProcessForm(true)}
									   pendingInvoiceData={enrichedInvoiceData}
									   selectedInvoiceTypeId={selectedInvoiceTypeId}
									   setSelectedInvoiceTypeId={setSelectedInvoiceTypeId}
									   userCanManageInvoices={userCanManageInvoices}
				/>}
			{showStartRegularInvoiceBillingProcessForm && <StartRegularInvoiceBillingProcessForm allUsersData={allUsers}
																								 onCancel={() => setShowStartBillingProcessForm(false)}
																								 onSuccess={handleSuccess}
																								 pendingInvoiceData={enrichedInvoiceData} />}

			{showUpdateSelfBilledInvoiceStatusForm &&
				<ProcessSelfBilledDraftInvoiceForm allUsersData={allUsers}
												   onCancel={() => setShowStartBillingProcessForm(false)}
												   onSuccess={handleSuccess}
												   pendingInvoiceData={enrichedInvoiceData} />}


		</Sidebar>
	);
};

export default PendingInvoiceDetailsSidebar;
